import React, { useState } from 'react';
import { User, Phone, Mail, Lock, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

// Add custom styles for PhoneInput in dark/light mode
const phoneInputStyles = `
  .phone-input-dark .PhoneInputInput {
    background-color: #374151 !important;
    color: #f3f4f6 !important;
    border-color: #4b5563 !important;
    border-radius: 6px !important;
    padding: 8px 12px !important;
  }
  .phone-input-light .PhoneInputInput {
    background-color: #ffffff !important;
    color: #111827 !important;
    border-color: #d1d5db !important;
    border-radius: 6px !important;
    padding: 8px 12px !important;
  }
  .phone-input-dark .PhoneInputInput.error {
    border-color: #ef4444 !important;
  }
  .phone-input-light .PhoneInputInput.error {
    border-color: #ef4444 !important;
  }
  .phone-input-dark .PhoneInputCountrySelect {
    background-color: #374151 !important;
    color: #f3f4f6 !important;
    border-color: #4b5563 !important;
  }
  .phone-input-light .PhoneInputCountrySelect {
    background-color: #f3f4f6 !important;
    color: #111827 !important;
    border-color: #d1d5db !important;
  }
  .phone-input-dark .PhoneInputCountryIcon {
    background-color: #374151 !important;
  }
  .phone-input-light .PhoneInputCountryIcon {
    background-color: #f3f4f6 !important;
  }
  .PhoneInputInput--focused {
    outline: none !important;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2) !important;
  }
`;

const ProfileSection = ({ 
  user, 
  isDarkMode = false, 
  onUpdateSuccess, 
  isPhoneRequired = false 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phoneNumber: user?.phoneNumber || '',
    email: user?.email || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});

  const validatePhone = (phone) => {
    if (!phone && isPhoneRequired) {
      return 'Phone number is required';
    }
    if (phone && (phone.length < 10 || phone.length > 20)) {
      return 'Phone number must be between 10 and 20 characters';
    }
    return null;
  };

  const validatePassword = (password, confirmPassword) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters';
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(password)) {
      return 'Password must contain uppercase, lowercase, number, and special character';
    }
    if (password !== confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate phone number
    const phoneError = validatePhone(formData.phoneNumber);
    if (phoneError) {
      newErrors.phoneNumber = phoneError;
    }

    // Validate required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const { authAPI } = await import('../../services/api');
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim()
      };
      
      // Always include phoneNumber if provided
      if (formData.phoneNumber) {
        payload.phoneNumber = formData.phoneNumber.trim();
      }
      
      const response = await authAPI.updateProfile(payload);

      if (response.success) {
        toast.success('Profile updated successfully');
        setIsEditing(false);
        if (onUpdateSuccess) {
          onUpdateSuccess(response.data.user);
        }
      } else {
        const errorMsg = response.message || response.errors?.[0]?.msg || 'Failed to update profile';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    const passwordError = validatePassword(
      passwordData.newPassword,
      passwordData.confirmPassword
    );
    
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    if (!passwordData.currentPassword) {
      toast.error('Current password is required');
      return;
    }

    setLoading(true);
    try {
      const { authAPI } = await import('../../services/api');
      const response = await authAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.success) {
        toast.success('Password changed successfully');
        setIsChangingPassword(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast.error(response.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Information Section */}
      <div className={`rounded-lg shadow-lg border transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-skyone-card-dark border-skyone-border-dark' 
          : 'bg-white border-gray-200'
      }`}>
        <div className={`px-6 py-4 border-b transition-colors duration-300 ${
          isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-xl font-semibold flex items-center transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <User className="w-5 h-5 mr-2" />
              Profile Information
            </h2>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  isDarkMode
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>

        <div className="px-6 py-4">
          {isEditing ? (
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* First Name */}
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 rounded-md border transition-colors duration-300 ${
                      errors.firstName
                        ? 'border-red-500'
                        : isDarkMode
                          ? 'bg-skyone-inset-dark border-skyone-border-dark text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 rounded-md border transition-colors duration-300 ${
                      errors.lastName
                        ? 'border-red-500'
                        : isDarkMode
                          ? 'bg-skyone-inset-dark border-skyone-border-dark text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Phone Number */}
              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone Number {isPhoneRequired && <span className="text-red-500">*</span>}
                </label>
                <div className={isDarkMode ? 'phone-input-dark' : 'phone-input-light'}>
                  <style>{phoneInputStyles}</style>
                  <PhoneInput
                    international
                    defaultCountry="US"
                    value={formData.phoneNumber}
                    onChange={(value) => setFormData(prev => ({ ...prev, phoneNumber: value || '' }))}
                    className={errors.phoneNumber ? 'error' : ''}
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="mt-1 text-sm text-red-500">{errors.phoneNumber}</p>
                )}
              </div>

              {/* Email (Read-only) */}
              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className={`w-full px-3 py-2 rounded-md border transition-colors duration-300 ${
                    isDarkMode
                      ? 'bg-skyone-inset-dark border-skyone-border-dark text-gray-400'
                      : 'bg-gray-100 border-gray-300 text-gray-500'
                  }`}
                />
                <p className={`mt-1 text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Email cannot be changed
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex items-center px-4 py-2 font-medium rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDarkMode
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({
                      firstName: user?.firstName || '',
                      lastName: user?.lastName || '',
                      phoneNumber: user?.phoneNumber || '',
                      email: user?.email || ''
                    });
                    setErrors({});
                  }}
                  className={`px-4 py-2 font-medium rounded-md transition-colors duration-200 ${
                    isDarkMode
                      ? 'bg-skyone-inset-dark text-white hover:bg-skyone-border-dark'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    First Name
                  </p>
                  <p className={`mt-1 text-base transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {user?.firstName || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Last Name
                  </p>
                  <p className={`mt-1 text-base transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {user?.lastName || 'N/A'}
                  </p>
                </div>
              </div>
              <div>
                <p className={`text-sm font-medium flex items-center transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <Phone className="w-4 h-4 mr-1" />
                  Phone Number
                </p>
                <p className={`mt-1 text-base transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {user?.phoneNumber || 'Not set'}
                </p>
              </div>
              <div>
                <p className={`text-sm font-medium flex items-center transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <Mail className="w-4 h-4 mr-1" />
                  Email
                </p>
                <p className={`mt-1 text-base transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {user?.email || 'N/A'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Change Password Section */}
      <div className={`rounded-lg shadow-lg border transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-skyone-card-dark border-skyone-border-dark' 
          : 'bg-white border-gray-200'
      }`}>
        <div className={`px-6 py-4 border-b transition-colors duration-300 ${
          isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-xl font-semibold flex items-center transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <Lock className="w-5 h-5 mr-2" />
              Change Password
            </h2>
            {!isChangingPassword && (
              <button
                onClick={() => setIsChangingPassword(true)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  isDarkMode
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                Change Password
              </button>
            )}
          </div>
        </div>

        {isChangingPassword && (
          <div className="px-6 py-4">
            <form onSubmit={handlePasswordSave} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className={`w-full px-3 py-2 pr-10 rounded-md border transition-colors duration-300 ${
                      isDarkMode
                        ? 'bg-skyone-inset-dark border-skyone-border-dark text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className={`w-full px-3 py-2 pr-10 rounded-md border transition-colors duration-300 ${
                      isDarkMode
                        ? 'bg-skyone-inset-dark border-skyone-border-dark text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className={`mt-1 text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Must contain uppercase, lowercase, number, and special character
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className={`w-full px-3 py-2 pr-10 rounded-md border transition-colors duration-300 ${
                      isDarkMode
                        ? 'bg-skyone-inset-dark border-skyone-border-dark text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex items-center px-4 py-2 font-medium rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDarkMode
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsChangingPassword(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className={`px-4 py-2 font-medium rounded-md transition-colors duration-200 ${
                    isDarkMode
                      ? 'bg-skyone-inset-dark text-white hover:bg-skyone-border-dark'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSection;
